import { Box, Center, Container, Image, Loader, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";
import classes from "./LoginPage.module.css";

const ADD_PAY_LOGO_URL =
  "https://resources.addpay.cloud/ADDPAY/logos/addpay_logo_vertical.png";

function getEmbeddedIframeCssUrl() {
  return `/app/embeddediframe.css`;
}

function getEmbeddedContentHeight(iframe: HTMLIFrameElement): number | null {
  try {
    const doc = iframe.contentDocument;
    if (!doc?.body) return null;

    const container = doc.querySelector(".signin_container");
    const measureEl = container ?? doc.body;

    return Math.ceil(
      Math.max(
        measureEl.scrollHeight,
        measureEl.getBoundingClientRect().height,
        doc.documentElement.scrollHeight,
        doc.body.scrollHeight
      )
    );
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isEmbedLoading, setIsEmbedLoading] = useState(true);

  useEffect(() => {
    const config = {
      is_customize_forgot_password: false,
      forgot_password_id: "login",
      css_url: "/app/embeddediframe.css",
      forgot_password_css_url: "/app/passwordReset.css"
    };

    window.catalyst.auth.signIn("login", config);
  }, []);

  useEffect(() => {
    const loginHost = document.getElementById("login");
    if (!loginHost) return;

    let docObserver: MutationObserver | null = null;
    let activeIframe: HTMLIFrameElement | null = null;
    let lastAppliedHeight = -1;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;

    const applyHostLayout = () => {
      Object.assign(loginHost.style, {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxWidth: "100%",
        margin: "0 auto",
        height: "auto",
        minHeight: "0",
      });
    };

    const applyIframeHeight = (iframe: HTMLIFrameElement) => {
      const contentHeight = getEmbeddedContentHeight(iframe);
      if (!contentHeight) return;

      const height = Math.min(contentHeight, window.innerHeight - 48);
      if (lastAppliedHeight === height) return;

      lastAppliedHeight = height;
      iframe.style.height = `${height}px`;
      iframe.style.minHeight = "0";
      iframe.style.maxHeight = `${window.innerHeight - 48}px`;
    };

    const scheduleResize = (iframe: HTMLIFrameElement) => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = null;
          applyIframeHeight(iframe);
        });
      }, 100);
    };

    const teardownIframeObservers = () => {
      docObserver?.disconnect();
      docObserver = null;
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      if (rafId !== null) cancelAnimationFrame(rafId);
      debounceTimer = null;
      rafId = null;
      lastAppliedHeight = -1;
    };

    const markEmbedReady = (iframe: HTMLIFrameElement) => {
      try {
        const doc = iframe.contentDocument;
        const hasContent =
          !!doc?.querySelector(".signin_container") ||
          (doc?.body?.childElementCount ?? 0) > 0;
        if (hasContent) setIsEmbedLoading(false);
      } catch {
        setIsEmbedLoading(false);
      }
    };

    const setupIframe = (iframe: HTMLIFrameElement) => {
      if (activeIframe === iframe) return;
      teardownIframeObservers();
      activeIframe = iframe;
      setIsEmbedLoading(true);

      applyHostLayout();

      Object.assign(iframe.style, {
        width: "100%",
        border: "0",
        display: "block",
        margin: "auto",
        background: "transparent",
        overflow: "hidden",
      });

      const syncHeight = () => {
        scheduleResize(iframe);
        markEmbedReady(iframe);
      };

      const onIframeLoad = () => syncHeight();

      iframe.addEventListener("load", onIframeLoad);

      try {
        const doc = iframe.contentDocument;
        if (doc?.body) {
          docObserver = new MutationObserver(syncHeight);
          docObserver.observe(doc.body, {
            childList: true,
            subtree: true,
            attributes: true,
          });
        }
      } catch {
        // iframe document not accessible yet
      }

      syncHeight();
      window.setTimeout(syncHeight, 300);
      window.setTimeout(syncHeight, 800);
      window.setTimeout(() => setIsEmbedLoading(false), 10000);
    };

    const onWindowResize = () => {
      if (activeIframe) scheduleResize(activeIframe);
    };

    const onMessage = (event: MessageEvent) => {
      if (!activeIframe || event.source !== activeIframe.contentWindow) return;

      const height =
        typeof event.data === "number"
          ? event.data
          : event.data?.height ?? event.data?.frameHeight ?? event.data?.iframeHeight;

      if (typeof height === "number" && height > 0) {
        const capped = Math.min(height, window.innerHeight - 48);
        if (lastAppliedHeight !== capped) {
          lastAppliedHeight = capped;
          activeIframe.style.height = `${capped}px`;
        }
      }
    };

    const hostObserver = new MutationObserver(() => {
      const iframe = loginHost.querySelector("iframe");
      if (iframe instanceof HTMLIFrameElement) setupIframe(iframe);
    });

    hostObserver.observe(loginHost, { childList: true, subtree: true });

    const existingIframe = loginHost.querySelector("iframe");
    if (existingIframe instanceof HTMLIFrameElement) {
      setupIframe(existingIframe);
    }

    window.addEventListener("resize", onWindowResize);
    window.addEventListener("message", onMessage);

    return () => {
      hostObserver.disconnect();
      teardownIframeObservers();
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("message", onMessage);
      activeIframe = null;
    };
  }, [isMobile]);

  return (
    <Stack
      className={classes.loginPage}
      align="center"
      justify="center"
      h="100dvh"
      w="100%"
    >
      <Stack
        align="center"
        gap="lg"
        className={classes.loginColumn}
        w={isMobile ? "100%" : 500}
        maw="100%"
        px="md"
      >
        <Image
          src={ADD_PAY_LOGO_URL}
          alt="AddPay"
          className={classes.logo}
          fit="contain"
        />
        <Box className={classes.embedHost}>
          {isEmbedLoading && (
            <Center className={classes.embedLoader}>
              <Loader color="#00DDB3" size="lg" type="dots" />
            </Center>
          )}
          <Container
            id="login"
            p={0}
            w="100%"
            className={isEmbedLoading ? classes.loginHidden : undefined}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
